import React, { useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, GlobeIcon, PlusCircleIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useDomainPricing } from '../../../hooks/useDomainPricing';
import DomainDefaultMatrixTab from './DomainDefaultMatrixTab';
import DomainClientOverridesTab from './DomainClientOverridesTab';
import AddTldModal from './AddTldModal';

type DomainTabKey = 'default' | 'client';

/**
 * Domain Pricing — an Ancalagon-staff-only Admin page for the domain operations pricing matrix
 * (operation × TLD). Two tabs:
 *   - Default prices: the platform-default cells (/admin/products/domains), staff-editable directly.
 *   - Client overrides: the in-scope (impersonated) client's per-cell overrides (/prices/domains),
 *     editable only while impersonating a client.
 *
 * Gating (defense in depth): the route is adminOnly (hidden from the nav for non-staff) and this page
 * guards itself — a non-staff user who types the URL gets <NotFound />, the same as a missing route.
 */
function DomainPricing() {
  const auth = useAuth();
  const user = useUserContext();
  // Defer the staff-only matrix GET until the staff check passes.
  const { operations, tlds, cells, loading, error, refetch, applyCell } = useDomainPricing(
    user?.is_ancalagon_staff === true,
  );

  const [activeTabKey, setActiveTabKey] = useState<DomainTabKey>('default');
  const [isAddTldOpen, setIsAddTldOpen] = useState(false);

  // Only active TLDs are priceable (the cell endpoints reject inactive ones), so they form the rows.
  // This also hides any default cell that exists on an *inactive* TLD — safe today because no
  // TLD-deactivation path exists (TLDs are only ever added, active). If deactivation is added later,
  // surface inactive TLDs' dormant cells (read-only + clear) here, or clear them backend-side on deactivate.
  const activeTlds = useMemo(() => tlds.filter((t) => t.active), [tlds]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  if (user?.is_ancalagon_staff !== true) {
    return <NotFound />;
  }

  const renderContent = () => {
    if (loading && operations.length === 0) {
      return <CenteredSpinner />;
    }

    // Full error state only on a cold load (nothing to show). A warm Refresh failure keeps the tabs +
    // table and surfaces the error inline within the default tab (mirrors the client-overrides tab).
    if (error && operations.length === 0) {
      return (
        <EmptyState
          titleText="Unable to load domain pricing"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <Tabs
        activeKey={activeTabKey}
        onSelect={(_event, tabKey) => setActiveTabKey(tabKey as DomainTabKey)}
        mountOnEnter
        aria-label="Domain pricing sections"
      >
        <Tab eventKey="default" title={<TabTitleText>Default prices</TabTitleText>}>
          <div className="pf-v6-u-pt-md">
            <DomainDefaultMatrixTab
              operations={operations}
              tlds={activeTlds}
              cells={cells}
              loading={loading}
              error={error}
              refetch={refetch}
              applyCell={applyCell}
              onAddTld={() => setIsAddTldOpen(true)}
            />
          </div>
        </Tab>
        <Tab eventKey="client" title={<TabTitleText>Client overrides</TabTitleText>}>
          <div className="pf-v6-u-pt-md">
            <DomainClientOverridesTab operations={operations} tlds={activeTlds} />
          </div>
        </Tab>
      </Tabs>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem isActive>Domain Pricing</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <GlobeIcon />
              </Icon>
              Domain Pricing
            </Content>
            <Content component="p">
              Set per-TLD prices for domain operations — platform defaults and per-client overrides.
            </Content>
          </FlexItem>
          {activeTabKey === 'default' && operations.length > 0 && (
            <FlexItem>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsAddTldOpen(true)}>
                Add TLD
              </Button>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
      {isAddTldOpen && (
        <AddTldModal isOpen onClose={() => setIsAddTldOpen(false)} onAdded={() => void refetch()} />
      )}
    </>
  );
}

export { DomainPricing };
